import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, StarOff, Loader2, Filter } from "lucide-react";
import { adminApi } from "@/lib/admin";
/**
 * Curation panel — the missing link in the "great replies → training
 * corpus" loop.
 *
 * What the admin sees: recent user→assistant exchanges (default:
 * research invitees only, last 30 days). Each row shows the user's
 * question and HBM's reply side-by-side (stacked on mobile). One click
 * on Promote adds it to `hbm_corpus` with `dataset_name=curated_gold`
 * and `importance=1.0` — RAG retrieval will surface it on similar
 * future queries.
 *
 * Second click flips to Unpromote (idempotent removal).
 */
export default function CurationSection() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  // "invited_and_admins" (default, combined) | "invited" | "admins" |
  // "self" | "all". The combined default is what an admin normally
  // wants — their own sharpest chats + research invitees' chats — all
  // in one triage view, without pulling in non-consenting users.
  const [pool, setPool] = useState("invited_and_admins");
  const [sinceDays, setSinceDays] = useState(30);
  const [note, setNote] = useState({});          // conv|idx → note text
  const [busy, setBusy] = useState({});          // conv|idx → true while promoting

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminApi.listCurationExchanges({ limit: 50, pool, sinceDays });
      setRows(d?.exchanges || []);
    } catch (err) {
      toast.error(err.message || "Couldn't load exchanges.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [pool, sinceDays]);

  useEffect(() => {
    load();
  }, [load]);

  const keyOf = (r) => `${r.conv_id}|${r.index}`;

  const onPromote = async (r) => {
    const k = keyOf(r);
    if (busy[k]) return;
    setBusy((b) => ({ ...b, [k]: true }));
    try {
      await adminApi.promoteExchange({
        conv_id: r.conv_id,
        index: r.index,
        note: note[k] || undefined,
      });
      setRows((prev) =>
        (prev || []).map((x) => (keyOf(x) === k ? { ...x, promoted: true } : x))
      );
      toast.success("Promoted to gold corpus");
    } catch (err) {
      toast.error(err.message || "Promotion failed.");
    } finally {
      setBusy((b) => ({ ...b, [k]: false }));
    }
  };

  const onUnpromote = async (r) => {
    const k = keyOf(r);
    if (busy[k]) return;
    setBusy((b) => ({ ...b, [k]: true }));
    try {
      await adminApi.unpromoteExchange({ conv_id: r.conv_id, index: r.index });
      setRows((prev) =>
        (prev || []).map((x) => (keyOf(x) === k ? { ...x, promoted: false } : x))
      );
      toast.success("Removed from gold corpus");
    } catch (err) {
      toast.error(err.message || "Removal failed.");
    } finally {
      setBusy((b) => ({ ...b, [k]: false }));
    }
  };

  return (
    <div className="space-y-5" data-testid="curation-section">
      <p className="font-sans text-sm text-[var(--text-muted)] leading-relaxed">
        Turn great real chats into training corpus. Promoted exchanges land in{" "}
        <span className="text-[var(--accent)]">hbm_corpus</span> with{" "}
        <span className="font-mono-ui text-[11px]">dataset_name = curated_gold</span>{" "}
        and{" "}
        <span className="font-mono-ui text-[11px]">importance = 1.0</span> —
        RAG retrieval will surface them on similar future queries, so the
        HBM voice sharpens with every promotion.
      </p>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 border-t border-[var(--border-soft)] pt-4 pb-2">
        <Filter size={14} className="text-[var(--text-dim)]" />
        <label className="flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-muted)]">
          Source
          <select
            value={pool}
            onChange={(e) => setPool(e.target.value)}
            className="bg-[var(--bg-pill)] border border-[var(--border)] px-2 py-1 text-[var(--text)]"
            data-testid="curation-filter-pool"
          >
            <option value="invited_and_admins">Admins + invitees</option>
            <option value="invited">Research invitees</option>
            <option value="admins">All admins</option>
            <option value="self">Just me</option>
            <option value="all">Everyone</option>
          </select>
        </label>
        <label className="flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-muted)]">
          Last
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(Number(e.target.value))}
            className="bg-[var(--bg-pill)] border border-[var(--border)] px-2 py-1 text-[var(--text)]"
            data-testid="curation-filter-since-days"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
        </label>
        <button
          onClick={load}
          className="ml-auto px-3 py-1 border border-[var(--border)] hover:border-[var(--accent)] font-mono-ui text-[10px] tracking-[0.22em] uppercase transition-colors"
          data-testid="curation-refresh"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="font-sans text-sm text-[var(--text-dim)] py-6">Loading…</p>
      ) : !rows || rows.length === 0 ? (
        <p className="font-sans text-sm text-[var(--text-dim)] py-6">
          No exchanges found in the selected window. Try widening the date
          range or unchecking &quot;Research invitees only&quot; to see everyone&apos;s chats.
        </p>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => {
            const k = keyOf(r);
            const isBusy = !!busy[k];
            return (
              <div
                key={k}
                data-testid={`curation-row-${r.conv_id}-${r.index}`}
                className={`border p-4 space-y-3 ${
                  r.promoted ? "border-[var(--accent)]" : "border-[var(--border)]"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)] flex items-center gap-3">
                    <span>{r.conv_title}</span>
                    <span>·</span>
                    <span>{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}</span>
                    {r.star_count > 0 && (
                      <span
                        className="inline-flex items-center gap-1 text-[var(--accent)]"
                        title={`${r.star_count} user${r.star_count === 1 ? "" : "s"} starred this`}
                        data-testid={`curation-star-count-${r.conv_id}-${r.index}`}
                      >
                        <Star size={10} fill="currentColor" strokeWidth={0} />
                        {r.star_count}
                      </span>
                    )}
                    {r.promoted && (
                      <span className="text-[var(--accent)]">· Gold</span>
                    )}
                  </div>
                  {r.promoted ? (
                    <button
                      onClick={() => onUnpromote(r)}
                      disabled={isBusy}
                      className="px-3 py-1.5 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] font-mono-ui text-[10px] tracking-[0.22em] uppercase inline-flex items-center gap-1.5 transition-colors disabled:opacity-40"
                      data-testid={`curation-unpromote-${r.conv_id}-${r.index}`}
                    >
                      {isBusy ? <Loader2 size={11} className="animate-spin" /> : <StarOff size={11} />}
                      Unpromote
                    </button>
                  ) : (
                    <button
                      onClick={() => onPromote(r)}
                      disabled={isBusy}
                      className="px-3 py-1.5 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] font-mono-ui text-[10px] tracking-[0.22em] uppercase inline-flex items-center gap-1.5 transition-colors disabled:opacity-40"
                      data-testid={`curation-promote-${r.conv_id}-${r.index}`}
                    >
                      {isBusy ? <Loader2 size={11} className="animate-spin" /> : <Star size={11} />}
                      Promote
                    </button>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)] mb-1">
                      Question
                    </div>
                    <div className="text-sm text-[var(--text)] whitespace-pre-wrap leading-relaxed">
                      {r.question}
                    </div>
                  </div>
                  <div>
                    <div className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)] mb-1">
                      HBM reply
                    </div>
                    <div className="text-sm text-[var(--text)] whitespace-pre-wrap leading-relaxed">
                      {r.answer}
                    </div>
                  </div>
                </div>
                {!r.promoted && (
                  <input
                    type="text"
                    placeholder="Optional note (why is this gold?)"
                    value={note[k] || ""}
                    onChange={(e) => setNote((n) => ({ ...n, [k]: e.target.value }))}
                    className="w-full bg-[var(--bg-pill)] border border-[var(--border)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--accent)] outline-none"
                    data-testid={`curation-note-${r.conv_id}-${r.index}`}
                    maxLength={500}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
